import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_BTCH_INFO_FORM,
    SG_BTCH_INFO_PRMT_LIST,
} from "@/tra/tancis/ptli/btch/info/service/PtliBtchInfoMgmtService";

/*
 * Manage batch information registration
 * !== 배치정보관리 등록 ==!
 */
export const PTLI1201002S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM", "auth_ptli0047", "auth_ptli0048"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" },
            { area: "comnCd", comnCd: "CGM0176" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Manage batch information Form !== 배치정보관리 Form ==!
        btchInfoForm: useForm({
            defaultSchema: SF_BTCH_INFO_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        btchInfoPrmtList: useGrid({
            defaultSchema: SG_BTCH_INFO_PRMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Batch !== 배치 등록 ==!
        createBtchInfo: useFetch({
            api: (data) => APIS.createBtchInfo({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli1201001q);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.btchInfoForm.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.00006", // An error occurred during processing.
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // on Save Button Click Event Handler  !== 배치 등록 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.btchInfoForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            const arrBtchInfoPrmt = grid.btchInfoPrmtList.getData();
            const arrRow: String[] = [];

            // Check for duplicate batch parameter names !== 배치 파라미터명 중복 검사 ==!
            arrBtchInfoPrmt.forEach((objPrmt) => {
                if (!comnUtils.isEmpty(objPrmt.btchPrmtNm)) {
                    arrRow.push(objPrmt.btchPrmtNm);
                }
            });

            const setRow = new Set(arrRow);
            if (setRow.size < arrRow.length) {
                toast.showToast({ type: "warning", content: "msg.00005" }); // Duplicate data exists.., !== 중복된 데이터가 존재합니다.. ==!
                return;
            }

            form.btchInfoForm.handleSubmit(
                () => {
                    const data = form.btchInfoForm.getValues();
                    const btchPrmtList = grid.btchInfoPrmtList.getData();

                    // Setting batch parameter list !== 배치 파라미터 목록 세팅 ==!
                    data.btchPrmtList = btchPrmtList;

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createBtchInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Delete batch parameter list deletion event !== 배치파라미터 목록 삭제 이벤트 ==!
        click_Btn_DeleteGridBtchInfoPrmtList: () => {
            const seltList: any[] = grid.btchInfoPrmtList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.00004" });
                return;
            }

            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    grid.btchInfoPrmtList.deleteRow("checkbox");
                },
            });
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        edit: {},
    };

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.btchNm} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.btchDesc} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.bsopClsfCd} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.btchInfoForm.schema.onlnExcutnPsblYn}
                                controlSize={10}
                                options={[
                                    { label: "L_CD_YES", value: "Y" },
                                    { label: "L_CD_NO", value: "N" },
                                ]}
                            />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_BTCH_PRMT_LIST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    grid.btchInfoPrmtList.addRow();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridBtchInfoPrmtList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.btchInfoPrmtList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli1201001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onSaveBtnClick();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
